import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { ImageUpload } from './ImageUpload';
import {
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
  Link2,
  Sparkles,
  Users,
  Copy,
  ExternalLink,
  Trash2,
  Check,
  Image as ImageIcon,
  Plus,
  Share2,
  Network as NetworkIcon,
  FileDown,
  MessageCircle,
} from 'lucide-react';
import { LeaderGenealogy } from './LeaderGenealogy';
import { LinkMetaSheet } from './LinkMetaSheet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const apiBase = (BACKEND_URL || '').replace(/\/$/, '');

const StatCard = ({ label, value, sub, accent = 'slate' }) => {
  const ringMap = {
    emerald: 'ring-emerald-200 bg-emerald-50/60',
    indigo: 'ring-indigo-200 bg-indigo-50/60',
    rose: 'ring-rose-200 bg-rose-50/60',
    amber: 'ring-amber-200 bg-amber-50/60',
    slate: 'ring-slate-200 bg-slate-50/60',
  };
  return (
    <div className={`rounded-lg ring-1 p-3 ${ringMap[accent] || ringMap.slate}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-2xl font-black text-slate-900 mt-0.5 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
};

const TrendBadge = ({ value }) => {
  if (value > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
        <TrendingUp className="h-3 w-3" /> +{value}%
      </span>
    );
  }
  if (value < 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700">
        <TrendingDown className="h-3 w-3" /> {value}%
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500">
      <Minus className="h-3 w-3" /> 0%
    </span>
  );
};

// Live WhatsApp link preview card — moved to `LinkMetaSheet.jsx` (unified).
// Kept only unused imports pruned. See LinkMetaSheet for the shared UI.

export const LeaderDetailsDrawer = ({
  leader,           // productivity row (lideranca_id, nome, ...)
  activeTenant,
  open,
  onOpenChange,
  initialTab = 'performance',
  onChange,         // callback after any save/delete to refresh parent
  canEdit = true,
}) => {
  const [tab, setTab] = useState(initialTab);

  // Reset tab whenever drawer opens for a (different) leader
  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab, leader?.lideranca_id]);

  // ---- Links tab state ----
  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const fetchLinks = useCallback(async () => {
    if (!leader?.lideranca_id) return;
    setLinksLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/links`, { withCredentials: true });
      const filtered = (data || []).filter((l) => l.lideranca_id === leader.lideranca_id);
      setLinks(filtered);
    } catch (_) {
      toast.error('Erro ao carregar links');
    } finally {
      setLinksLoading(false);
    }
  }, [leader?.lideranca_id]);

  // ---- Supporters tab state ----
  const [supporters, setSupporters] = useState([]);
  const [supportersTotal, setSupportersTotal] = useState(0);
  const [supportersLoading, setSupportersLoading] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const fetchSupporters = useCallback(async () => {
    if (!leader?.lideranca_id) return;
    setSupportersLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/supporters/paginated`, {
        params: { lideranca_id: leader.lideranca_id, limit: 100, page: 1 },
        withCredentials: true,
      });
      setSupporters(data.items || []);
      setSupportersTotal(data.total || 0);
    } catch (_) {
      toast.error('Erro ao carregar apoiadores');
    } finally {
      setSupportersLoading(false);
    }
  }, [leader?.lideranca_id]);

  // ---- Captação tab state ----
  const [captureForm, setCaptureForm] = useState({ capture_hero_image_url: '', capture_subtitle: '' });
  const [captureSaving, setCaptureSaving] = useState(false);
  useEffect(() => {
    if (leader) {
      setCaptureForm({
        capture_hero_image_url: leader.capture_hero_image_url || '',
        capture_subtitle: leader.capture_subtitle || '',
      });
    }
  }, [leader]);

  // Reset tab data whenever a different leader is opened (state leak fix)
  useEffect(() => {
    setLinks([]);
    setSupporters([]);
    setSupportersTotal(0);
  }, [leader?.lideranca_id]);

  // Lazy-fetch each tab content on first open / when leader changes
  useEffect(() => {
    if (!open || !leader?.lideranca_id) return;
    if (tab === 'links' && links.length === 0 && !linksLoading) fetchLinks();
    if (tab === 'apoiadores' && supporters.length === 0 && !supportersLoading) fetchSupporters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, leader?.lideranca_id]);

  const saveCaptureMeta = async (clear = false) => {
    if (!leader) return;
    setCaptureSaving(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/leaders/${leader.lideranca_id}/capture-meta`,
        clear
          ? { capture_hero_image_url: null, capture_subtitle: null }
          : {
              capture_hero_image_url: captureForm.capture_hero_image_url || null,
              capture_subtitle: captureForm.capture_subtitle || null,
            },
        { withCredentials: true }
      );
      toast.success(clear ? 'Personalização removida.' : 'Personalização salva!');
      if (clear) setCaptureForm({ capture_hero_image_url: '', capture_subtitle: '' });
      if (onChange) onChange();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Falha ao salvar.');
    } finally {
      setCaptureSaving(false);
    }
  };

  // ---- Per-link meta edit — delegated to the shared <LinkMetaSheet> ----
  const [linkMetaOpen, setLinkMetaOpen] = useState(false);
  const [linkMetaTarget, setLinkMetaTarget] = useState(null);
  const openLinkMeta = (link) => {
    setLinkMetaTarget(link);
    setLinkMetaOpen(true);
  };

  const buildPublicUrl = (token) =>
    token && activeTenant?.slug ? `${apiBase}/api/og/r/${activeTenant.slug}/${token}` : '';

  // Builds a wa.me deep link for direct 1:1 messaging. Assumes Brazilian
  // numbers when no country code is present (10/11 digits get "55" prefix).
  const buildWaMeUrl = (rawPhone, ldr) => {
    if (!rawPhone) return '';
    const digits = String(rawPhone).replace(/\D/g, '');
    if (!digits) return '';
    const withCc = (digits.length === 10 || digits.length === 11) && !digits.startsWith('55')
      ? `55${digits}` : digits;
    const inviter = ldr?.apelido || (ldr?.nome || '').split(' ')[0] || 'nossa rede';
    const msg = `Olá! Aqui é da equipe de ${inviter}. Tudo bem?`;
    return `https://wa.me/${withCc}?text=${encodeURIComponent(msg)}`;
  };

  const handleDownloadSupportersPdf = async () => {
    if (!leader?.lideranca_id) return;
    setPdfDownloading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/leaders/${leader.lideranca_id}/supporters/pdf`,
        { withCredentials: true, responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (leader.apelido || leader.nome || 'lideranca')
        .toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '');
      a.download = `apoiadores-${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('PDF gerado!');
    } catch (_) {
      toast.error('Falha ao gerar PDF.');
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleCopyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copiado!');
    } catch (_) {
      toast.error('Falha ao copiar.');
    }
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Excluir este link? Não é possível desfazer.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/links/${linkId}`, { withCredentials: true });
      toast.success('Link excluído');
      fetchLinks();
      if (onChange) onChange();
    } catch (_) {
      toast.error('Falha ao excluir');
    }
  };

  if (!leader) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl overflow-y-auto"
          data-testid="leader-details-drawer"
        >
          <SheetHeader>
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-white font-bold flex items-center justify-center shrink-0">
                {(leader.nome || '?').slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <SheetTitle className="font-outfit text-xl text-slate-900 truncate">
                  {leader.nome}
                </SheetTitle>
                <SheetDescription className="text-xs text-slate-500 mt-0.5">
                  {leader.apelido && <>@{leader.apelido} · </>}
                  {leader.estado} {leader.cidade}
                  {leader.bairro && <> · {leader.bairro}</>}
                  <span className="ml-2 inline-flex items-center rounded-full border border-slate-200 px-2 py-0.5 capitalize text-[10px] text-slate-600 bg-white">
                    {leader.tipo}
                  </span>
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <Tabs value={tab} onValueChange={setTab} className="mt-5">
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="performance" data-testid="leader-tab-performance" className="text-xs gap-1.5">
                <Activity className="h-3.5 w-3.5" /> Performance
              </TabsTrigger>
              <TabsTrigger value="links" data-testid="leader-tab-links" className="text-xs gap-1.5">
                <Link2 className="h-3.5 w-3.5" /> Links
              </TabsTrigger>
              <TabsTrigger value="captacao" data-testid="leader-tab-captacao" className="text-xs gap-1.5">
                <Sparkles className="h-3.5 w-3.5" /> Captação
              </TabsTrigger>
              <TabsTrigger value="apoiadores" data-testid="leader-tab-apoiadores" className="text-xs gap-1.5">
                <Users className="h-3.5 w-3.5" /> Apoiadores
              </TabsTrigger>
              <TabsTrigger value="rede" data-testid="leader-tab-rede" className="text-xs gap-1.5">
                <NetworkIcon className="h-3.5 w-3.5" /> Rede
              </TabsTrigger>
            </TabsList>

            {/* === Performance === */}
            <TabsContent value="performance" className="mt-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <StatCard label="Acessos" value={leader.total_acessos ?? 0} accent="indigo" sub="todo o período" />
                <StatCard label="Redirects" value={leader.total_redirecionamentos ?? 0} accent="emerald" sub="conversão p/ WhatsApp" />
                <StatCard label="Conversão" value={`${leader.taxa_conversao ?? 0}%`} accent="amber" sub="redirects / acessos" />
                <StatCard label="Apoiadores identificados" value={leader.supporters_identificados ?? 0} accent="rose" sub={`${leader.touches_total ?? 0} touches totais`} />
              </div>

              <div className="rounded-lg border border-slate-200 p-4 bg-white">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
                  Últimos 7 dias
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-3xl font-black text-slate-900 tabular-nums">{leader.acessos_7d ?? 0}</div>
                    <div className="text-xs text-slate-500 mt-0.5">acessos · {leader.redirects_7d ?? 0} redirects</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Tendência</div>
                    <TrendBadge value={leader.tendencia_7d ?? 0} />
                    <div className="text-[10px] text-slate-400 mt-0.5">vs. 7 dias anteriores</div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-lg bg-slate-50 ring-1 ring-slate-200 p-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Links</div>
                  <div className="text-lg font-black text-slate-800 tabular-nums">{leader.links_count ?? 0}</div>
                </div>
                <div className="rounded-lg bg-slate-50 ring-1 ring-slate-200 p-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Cartões NFC</div>
                  <div className="text-lg font-black text-slate-800 tabular-nums">{leader.cards_count ?? 0}</div>
                </div>
                <div className="rounded-lg bg-slate-50 ring-1 ring-slate-200 p-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Status</div>
                  <div className="text-sm font-bold mt-1 capitalize">
                    <span className={`inline-flex items-center gap-1 ${leader.status === 'ativo' ? 'text-emerald-700' : 'text-slate-500'}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${leader.status === 'ativo' ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                      {leader.status}
                    </span>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* === Links === */}
            <TabsContent value="links" className="mt-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500">
                  <strong className="text-slate-900">{links.length}</strong> link(s) desta liderança
                </div>
              </div>

              {linksLoading ? (
                <div className="text-sm text-slate-400 py-8 text-center">Carregando…</div>
              ) : links.length === 0 ? (
                <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-lg">
                  Esta liderança ainda não tem links.
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 hover:bg-slate-50">
                        <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Token</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Tipo</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600 text-right">Acessos</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600 text-right">Redirects</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600 text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {links.map((link) => {
                        const hasCustomMeta = !!(link.og_title || link.og_description || link.og_image_url || link.capture_hero_image_url || link.capture_subtitle);
                        return (
                          <TableRow key={link.id} data-testid={`drawer-link-row-${link.id}`}>
                            <TableCell className="font-mono text-xs font-semibold text-slate-800">
                              /{link.token}
                              {hasCustomMeta && (
                                <Badge className="ml-1.5 bg-emerald-100 text-emerald-700 border-emerald-200 text-[9px] px-1.5">
                                  <Sparkles className="h-2.5 w-2.5 mr-0.5" /> custom
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-xs capitalize text-slate-700">{link.tipo_acao}</TableCell>
                            <TableCell className="text-xs text-right font-semibold tabular-nums">{link.stats?.total_acessos ?? 0}</TableCell>
                            <TableCell className="text-xs text-right font-semibold tabular-nums">{link.stats?.total_redirecionamentos ?? 0}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-0.5">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => handleCopyLink(buildPublicUrl(link.token))}
                                  data-testid={`drawer-copy-${link.id}`}
                                  title="Copiar URL"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => window.open(buildPublicUrl(link.token), '_blank')}
                                  data-testid={`drawer-open-${link.id}`}
                                  title="Abrir link"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={`h-7 w-7 ${hasCustomMeta ? 'text-emerald-700' : ''}`}
                                  onClick={() => openLinkMeta(link)}
                                  disabled={!canEdit}
                                  data-testid={`drawer-edit-meta-${link.id}`}
                                  title="Personalizar OG/captação"
                                >
                                  <Sparkles className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-rose-600 hover:bg-rose-50"
                                  onClick={() => handleDeleteLink(link.id)}
                                  disabled={!canEdit}
                                  data-testid={`drawer-delete-${link.id}`}
                                  title="Excluir"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              <div className="text-[11px] text-slate-500 leading-relaxed pt-1">
                Para criar um novo link específico desta liderança, use a página de Links no menu lateral
                (em breve essa ação será embutida aqui).
              </div>
            </TabsContent>

            {/* === Captação === */}
            <TabsContent value="captacao" className="mt-5 space-y-5">
              <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 text-xs text-emerald-900 leading-relaxed">
                <strong>Ordem de prioridade:</strong> link específico → liderança (aqui) → padrão do cliente → padrão MeAdd. Em branco, herdamos do cliente.
              </div>

              {/* Live preview */}
              <div className="rounded-lg border border-slate-200 overflow-hidden bg-white shadow-sm">
                {captureForm.capture_hero_image_url ? (
                  <img
                    src={captureForm.capture_hero_image_url}
                    alt="Cabeçalho"
                    className="w-full aspect-[1200/628] object-cover"
                  />
                ) : (
                  <div className="w-full aspect-[1200/628] flex items-center justify-center bg-gradient-to-br from-emerald-50 to-emerald-100/60">
                    <div className="text-center text-slate-400">
                      <ImageIcon className="h-10 w-10 mx-auto" />
                      <p className="text-xs mt-2">Sem cabeçalho — usará padrão do cliente</p>
                    </div>
                  </div>
                )}
                <div className="p-4">
                  <div className="text-xl font-black tracking-tight text-slate-900">Você foi convidado(a)!</div>
                  <div className="text-xs text-slate-700 mt-1.5">
                    <strong>{leader.nome}</strong> está te convidando para participar da rede.
                  </div>
                  <div className="text-xs text-slate-500 mt-2 whitespace-pre-line">
                    {captureForm.capture_subtitle || 'Preencha seus dados para continuar e entrar no grupo/canal do WhatsApp.'}
                  </div>
                </div>
              </div>

              <ImageUpload
                value={captureForm.capture_hero_image_url}
                onChange={(url) => setCaptureForm({ ...captureForm, capture_hero_image_url: url })}
                folder={`meadd/tenants/${activeTenant?.id}/leaders/${leader.lideranca_id}`}
                label="Imagem do cabeçalho (1200×628)"
                aspect="aspect-[1200/628]"
                maxWidth="max-w-full"
                testId="leader-capture-hero-drawer"
              />

              <div>
                <Label className="text-sm font-semibold text-slate-700">
                  Mensagem antes dos campos <span className="text-slate-400 font-normal">(até 240 caracteres)</span>
                </Label>
                <Textarea
                  value={captureForm.capture_subtitle}
                  onChange={(e) => setCaptureForm({ ...captureForm, capture_subtitle: e.target.value.slice(0, 240) })}
                  placeholder={`Ex.: Olá! Sou ${leader.apelido || leader.nome?.split(' ')[0]}, vamos construir essa rede juntos.`}
                  rows={4}
                  className="mt-1.5 resize-none"
                  data-testid="leader-capture-subtitle-drawer"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  {captureForm.capture_subtitle.length}/240 caracteres.
                </p>
              </div>

              <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => saveCaptureMeta(true)}
                  disabled={captureSaving || !canEdit || (!captureForm.capture_hero_image_url && !captureForm.capture_subtitle)}
                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 text-xs"
                  data-testid="leader-capture-clear-drawer"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Resetar
                </Button>
                <Button
                  type="button"
                  onClick={() => saveCaptureMeta(false)}
                  disabled={captureSaving || !canEdit}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="leader-capture-save-drawer"
                >
                  <Check className="h-4 w-4 mr-1.5" />
                  {captureSaving ? 'Salvando…' : 'Salvar'}
                </Button>
              </div>
            </TabsContent>

            {/* === Apoiadores === */}
            <TabsContent value="apoiadores" className="mt-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500">
                  <strong className="text-slate-900">{supportersTotal}</strong> apoiador(es) identificado(s)
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadSupportersPdf}
                  disabled={pdfDownloading || supportersTotal === 0}
                  className="h-8 gap-1.5 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  data-testid="drawer-supporters-pdf-btn"
                >
                  <FileDown className="h-3.5 w-3.5" />
                  {pdfDownloading ? 'Gerando…' : 'Exportar PDF'}
                </Button>
              </div>
              {supportersLoading ? (
                <div className="text-sm text-slate-400 py-8 text-center">Carregando…</div>
              ) : supporters.length === 0 ? (
                <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-lg">
                  Ainda sem apoiadores identificados.
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 hover:bg-slate-50">
                        <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Nome</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">WhatsApp</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Bairro</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600 w-[80px] text-right">Ação</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {supporters.map((s) => {
                        const waUrl = buildWaMeUrl(s.whatsapp, leader);
                        return (
                          <TableRow key={s.id} data-testid={`drawer-supporter-${s.id}`}>
                            <TableCell className="text-xs font-medium">{s.nome || <span className="text-slate-400 italic">Anônimo</span>}</TableCell>
                            <TableCell className="text-xs font-mono">{s.whatsapp || '—'}</TableCell>
                            <TableCell className="text-xs text-slate-600">{s.bairro || '—'}</TableCell>
                            <TableCell className="text-right">
                              {waUrl ? (
                                <a
                                  href={waUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 hover:text-emerald-800 hover:underline"
                                  data-testid={`drawer-supporter-wa-${s.id}`}
                                >
                                  <MessageCircle className="h-3 w-3" /> WhatsApp
                                </a>
                              ) : (
                                <span className="text-[11px] text-slate-300">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
              {supportersTotal > supporters.length && (
                <div className="text-[11px] text-slate-400 text-center">
                  Mostrando {supporters.length} de {supportersTotal}. Para ver todos, abra a página de Apoiadores.
                </div>
              )}
            </TabsContent>

            {/* === Rede (genealogia hierárquica) === */}
            <TabsContent value="rede" className="mt-5 space-y-3">
              <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 text-xs text-emerald-900 leading-relaxed">
                <div className="font-semibold mb-1 flex items-center gap-1.5">
                  <Share2 className="h-3 w-3" /> Captação de lideranças por link
                </div>
                <div className="text-[11px] text-emerald-800">
                  Cada link desta liderança pode habilitar a página de cadastro de nova liderança (aba &ldquo;Links&rdquo; do drawer). Quem se cadastra ali entra na rede como indicado por <strong>{leader.nome}</strong>, e seu link automático segue para o <strong>mesmo destino</strong> do link de origem.
                </div>
              </div>

              <LeaderGenealogy leaderId={leader.lideranca_id} />
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      {/* Per-link meta edit — unified shared sheet (LinkMetaSheet) */}
      <LinkMetaSheet
        link={linkMetaTarget}
        leader={leader ? { apelido: leader.apelido, nome: leader.nome, nome_completo: leader.nome } : null}
        open={linkMetaOpen}
        onOpenChange={setLinkMetaOpen}
        onSaved={() => { setLinkMetaOpen(false); fetchLinks(); }}
        canEdit={canEdit}
      />
    </>
  );
};

export default LeaderDetailsDrawer;
